import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import Heading from "../../components/ui/Heading";
import SearchInput from "../../components/ui/SearchInput";
import api from "../../api/api";

import Button from '../../components/ui/Button';
Modal.setAppElement("#root");

/* ---------------- ICONS ---------------- */
const GridIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
);

const ListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="8" y1="6" x2="21" y2="6"></line>
    <line x1="8" y1="12" x2="21" y2="12"></line>
    <line x1="8" y1="18" x2="21" y2="18"></line>
    <line x1="3" y1="6" x2="3.01" y2="6"></line>
    <line x1="3" y1="12" x2="3.01" y2="12"></line>
    <line x1="3" y1="18" x2="3.01" y2="18"></line>
  </svg>
);

function Layout({ children }) {
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="px-2 py-4 mx-auto sm:px-6 max-w-7xl">{children}</div>
    </div>
  );
}

export default function App() {
  return (
    <Layout>
      <OnlineOrderPage />
    </Layout>
  );
}

function OnlineOrderPage() {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState("card"); // card | table
  const [convertedOrders, setConvertedOrders] = useState(
    JSON.parse(localStorage.getItem("convertedOrders") || "[]")
  );

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const username = "admin";
        const data = await api.OnlineSimplePOAPI(username);

        console.log("API Response:", data);
        console.log("Orders Table:", data?.Table);

        setOrders(data?.Table || []);
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to fetch orders. Please check your network connection.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const handleViewItem = async (order) => {
    try {
      // ✅ Call the wrapper function you already defined in api.js
      const res = await api.OnlineGetPOItemAPI(order.Poid);

      // ✅ Extract items correctly from response
      const items = res?.Table || [];


      if (items.length > 0) {
        console.log("Fetched items:", items); // debug log
        setSelectedItems(items);
        setSelectedCustomer(order.Customer);
        setIsModalOpen(true);
      } else {
        alert(`No items found for order: ${order.Poid}`);
      }
    } catch (err) {
      alert("Failed to fetch order items.");
      console.error("Fetch error:", err);
    }
  };

  const handleConvertOrder = async (order) => {
    try {
      const payload = {
        Poid: order.Poid,
        SqlAction: "Insert",
        UserName: order.UsrName || "Admin",
      };

      const result = await api.OnlinePoHead2SimplepoAPI(payload);

      alert("Order converted successfully.");

      const updatedConverted = [...convertedOrders, order.Poid];
      setConvertedOrders(updatedConverted);
      localStorage.setItem("convertedOrders", JSON.stringify(updatedConverted));
    } catch (err) {
      alert("Failed to convert order.");
      console.error("Convert Order Error:", err);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItems([]);
    setSelectedCustomer("");
  };

  const filteredOrders = useMemo(
    () =>
      orders.filter((order) =>
        order.Customer?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [orders, searchTerm]
  );

  if (loading)
    return <div className="mt-10 text-center text-gray-600">Loading...</div>;
  if (error)
    return <div className="mt-10 text-center text-red-600">Error: {error}</div>;

  return (
    <div className="p-3 bg-white rounded-lg shadow-xl sm:p-6">
      <Heading>Online Orders</Heading>

      <div className="flex flex-col gap-4 mb-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Search Input */}
        <div className="flex-grow">
          <SearchInput
            placeholder="Search customer name..."
            className="w-full p-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Toggle Buttons */}
        <div className="flex items-center gap-2 mt-2 sm:mt-0">
          <button
            onClick={() => setViewMode("card")}
            className={`flex items-center justify-center p-2 rounded-md transition-colors duration-200 ${
              viewMode === "card"
                ? "bg-white text-blue-600 shadow"
                : "text-gray-500"
            }`}
          >
            <GridIcon />
          </button>
          <button
            onClick={() => setViewMode("table")}
            className={`flex items-center justify-center p-2 rounded-md transition-colors duration-200 ${
              viewMode === "table"
                ? "bg-white text-blue-600 shadow"
                : "text-gray-500"
            }`}
          >
            <ListIcon />
          </button>
        </div>
      </div>

      {/* CARD VIEW */}
      {viewMode === "card" ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredOrders.length > 0 ? (
            filteredOrders.map((order) => (
              <div
                key={order.Poid}
                className="p-4 bg-white shadow-md rounded-xl hover:shadow-lg"
              >
                <div className="mb-2 font-semibold text-gray-800">
                  Order #{order.Poid}
                </div>
                <div className="text-gray-700">{order.Customer}</div>
                <div className="text-sm text-gray-500">{order.MobileNo}</div>
                <div className="mb-2 text-sm text-gray-500">
                  {order.Address}
                </div>
                <div className="mb-2">
                  Order Total: ₹{order.OrderTotal?.toFixed(2)}
                </div>
                <div className="mb-2">Pay Ref: {order.PayRef}</div>
                <div className="mb-2">
                  Amount: ₹{order.Amount?.toFixed(2)}
                </div>
                <div className="flex flex-col gap-2 mt-2">
                  {!convertedOrders.includes(order.Poid) && (
                    <button
                      onClick={() => handleConvertOrder(order)}
                      className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
                    >
                      Convert
                    </button>
                  )}
                  <button
                    onClick={() => handleViewItem(order)}
                    className="px-3 py-1 text-white bg-gray-600 rounded hover:bg-gray-700"
                  >
                    View Items
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center text-gray-500 col-span-full">
              No orders found.
            </div>
          )}
        </div>
      ) : (
        /* TABLE VIEW */
        <div className="overflow-x-auto rounded-lg shadow-md">
          <table className="min-w-full text-sm divide-y divide-gray-200">
            <thead className="text-gray-900 bg-gray-100">
              <tr>
                {[
                  "Order",
                  "Customer",
                  "Order Total",
                  "Pay Ref",
                  "Amount",
                  "Actions",
                ].map((head) => (
                  <th
                    key={head}
                    className="px-3 py-2 font-semibold text-left uppercase"
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => (
                  <tr key={order.Poid} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-800">
                      {order.Poid}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-800">
                        {order.Customer}
                      </div>
                      <div className="text-sm text-gray-600">
                        {order.MobileNo}
                      </div>
                      <div className="text-sm text-gray-500">
                        {order.Address}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      ₹{order.OrderTotal?.toFixed(2)}
                    </td>
                    <td className="px-3 py-2">{order.PayRef}</td>
                    <td className="px-3 py-2">
                      ₹{order.Amount?.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 space-y-1">
                      {!convertedOrders.includes(order.Poid) && (
                        <button
                          onClick={() => handleConvertOrder(order)}
                          className="w-full px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
                        >
                          Convert
                        </button>
                      )}
                      <button
                        onClick={() => handleViewItem(order)}
                        className="w-full px-3 py-1 text-white bg-gray-600 rounded hover:bg-gray-700"
                      >
                        View Items
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="p-6 text-center text-gray-500">
                    No orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal for viewing items */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Item Details"
        className="w-[95%] sm:max-w-2xl p-4 sm:p-6 mx-auto mt-20 bg-white border rounded-lg shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center"
      >
        <h2 className="mb-4 text-lg font-semibold sm:text-xl">
          Items for {selectedCustomer}
        </h2>
        <div className="overflow-x-auto max-h-80 sm:max-h-96">
          <table className="min-w-full text-sm border border-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 border">Sl No</th>
                <th className="px-3 py-2 border">Part No</th>
                <th className="px-3 py-2 border">Description</th>
                <th className="px-3 py-2 border">Qty</th>
                <th className="px-3 py-2 border">Rate</th>
                <th className="px-3 py-2 border">Amount</th>
              </tr>
            </thead>
            <tbody>
              {selectedItems.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-3 py-2 border text-center">
                    {index + 1}
                  </td>
                  <td className="px-3 py-2 border">{item.Partno}</td>
                  <td className="px-3 py-2 border">{item.Partdesc}</td>
                  <td className="px-3 py-2 border text-right">{item.Qty}</td>
                  <td className="px-3 py-2 border text-right">₹{item.Rate}</td>
                  <td className="px-3 py-2 border text-right">
                    ₹{item.Amount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          onClick={closeModal}
          className="px-3 py-1 mt-6 text-white bg-blue-600 rounded hover:bg-blue-700"
        >
          Close
        </button>
      </Modal>
    </div>
  );
}


// import React, { useEffect, useMemo, useState } from "react";
// import Modal from "react-modal";
// import Heading from "../../components/ui/Heading";
// import SearchInput from "../../components/ui/SearchInput";
// import Button from '../../components/ui/Button';
// Modal.setAppElement("#root");

// /* ---------------- ICONS ---------------- */
// const GridIcon = () => (
//   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
//     <rect x="3" y="3" width="7" height="7"></rect>
//     <rect x="14" y="3" width="7" height="7"></rect>
//     <rect x="14" y="14" width="7" height="7"></rect>
//     <rect x="3" y="14" width="7" height="7"></rect>
//   </svg>
// );

// const ListIcon = () => (
//   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
//     <line x1="8" y1="6" x2="21" y2="6"></line>
//     <line x1="8" y1="12" x2="21" y2="12"></line>
//     <line x1="8" y1="18" x2="21" y2="18"></line>
//     <line x1="3" y1="6" x2="3.01" y2="6"></line>
//     <line x1="3" y1="12" x2="3.01" y2="12"></line>
//     <line x1="3" y1="18" x2="3.01" y2="18"></line>
//   </svg>
// );

// function Layout({ children }) {
//   return (
//     <div className="min-h-screen bg-gray-100">
//       <div className="px-2 py-4 mx-auto sm:px-6 max-w-7xl">{children}</div>
//     </div>
//   );
// }

// export default function App() {
//   return (
//     <Layout>
//       <OnlineOrderPage />
//     </Layout>
//   );
// }

// function OnlineOrderPage() {
//   const [orders, setOrders] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedItems, setSelectedItems] = useState([]);
//   const [selectedCustomer, setSelectedCustomer] = useState("");
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [viewMode, setViewMode] = useState("card"); // card | table
//   const [convertedOrders, setConvertedOrders] = useState(
//     JSON.parse(localStorage.getItem("convertedOrders") || "[]")
//   );

//   useEffect(() => {
//     const fetchOrders = async () => {
//       try {
//         const username = "admin";
//         const url = `https://tradeservice.revampapps.com/OnlineSimplePOAPI?UserName=${username}`;
//         const response = await fetch(url);
//         if (!response.ok)
//           throw new Error(`HTTP error! status: ${response.status}`);
//         const data = await response.json();
//         setOrders(data?.OnlineSimplePOAPIjs?.Table || []);
//       } catch (err) {
//         setError("Failed to fetch orders. Please check your network connection.");
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchOrders();
//   }, []);

//   const handleViewItem = async (order) => {
//     try {
//       const response = await fetch(
//         `https://tradeservice.revampapps.com/OnlineGetPOItemAPI?Poid=${order.Poid}`
//       );
//       if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
//       const data = await response.json();
//       const items = data?.OnlineGetPOItemAPIjs?.Table || [];
//       if (items.length > 0) {
//         setSelectedItems(items);
//         setSelectedCustomer(order.Customer);
//         setIsModalOpen(true);
//       } else {
//         alert(`No items found for order: ${order.Poid}`);
//       }
//     } catch (err) {
//       alert("Failed to fetch order items.");
//       console.error("Fetch error:", err);
//     }
//   };

//   const handleConvertOrder = async (order) => {
//     try {
//       const payload = { Poid: order.Poid, SqlAction: "Insert", UserName: order.UsrName || "Admin" };
//       const response = await fetch(
//         "https://tradeservice.revampapps.com/OnlinePoHead2SimplepoAPI",
//         { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }
//       );
//       const result = await response.json();
//       if (response.ok) {
//         alert("Order converted successfully.");
//         const updatedConverted = [...convertedOrders, order.Poid];
//         setConvertedOrders(updatedConverted);
//         localStorage.setItem("convertedOrders", JSON.stringify(updatedConverted));
//       } else {
//         alert("Failed to convert order.");
//         console.error("Conversion Failed:", result);
//       }
//     } catch (err) {
//       alert("An error occurred while converting the order.");
//       console.error("Convert Order Error:", err);
//     }
//   };

//   const closeModal = () => {
//     setIsModalOpen(false);
//     setSelectedItems([]);
//     setSelectedCustomer("");
//   };

//   const filteredOrders = useMemo(() =>
//     orders.filter((order) =>
//       order.Customer?.toLowerCase().includes(searchTerm.toLowerCase())
//     ), [orders, searchTerm]);

//   if (loading) return <div className="mt-10 text-center text-gray-600">Loading...</div>;
//   if (error) return <div className="mt-10 text-center text-red-600">Error: {error}</div>;

//   return (
//     <div className="p-3 bg-white rounded-lg shadow-xl sm:p-6">
//       <Heading>Online Orders</Heading>

//      <div className="flex flex-col gap-4 mb-4 sm:flex-row sm:items-center sm:justify-between">
//   {/* Search Input */}
//   <div className="flex-grow">
//     <SearchInput
//       placeholder="Search customer name..."
//       className="w-full p-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//       value={searchTerm}
//       onChange={(e) => setSearchTerm(e.target.value)}
//     />
//   </div>

//   {/* Toggle Buttons */}
//   <div className="flex items-center gap-2 mt-2 sm:mt-0">
//     <button
//       onClick={() => setViewMode("card")}
//       className={`flex items-center justify-center p-2 rounded-md transition-colors duration-200 ${
//         viewMode === "card" ?  "bg-white text-blue-600 shadow" : "text-gray-500"
//       }`}
//     >
//       <GridIcon />
//     </button>
//     <button
//       onClick={() => setViewMode("table")}
//       className={`flex items-center justify-center p-2 rounded-md transition-colors duration-200 ${
//         viewMode === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"
//       }`}
//     >
//       <ListIcon />
//     </button>
//   </div>
// </div>


//       {/* CARD VIEW */}
//       {viewMode === "card" ? (
//         <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
//           {filteredOrders.length > 0 ? filteredOrders.map((order) => (
//             <div key={order.Poid} className="p-4 bg-white shadow-md rounded-xl hover:shadow-lg">
//               <div className="mb-2 font-semibold text-gray-800">Order #{order.Poid}</div>
//               <div className="text-gray-700">{order.Customer}</div>
//               <div className="text-sm text-gray-500">{order.MobileNo}</div>
//               <div className="text-sm text-gray-500 mb-2">{order.Address}</div>
//               <div className="mb-2">Order Total: ₹{order.OrderTotal?.toFixed(2)}</div>
//               <div className="mb-2">Pay Ref: {order.PayRef}</div>
//               <div className="mb-2">Amount: ₹{order.Amount?.toFixed(2)}</div>
//               <div className="flex flex-col gap-2 mt-2">
//                 {!convertedOrders.includes(order.Poid) && (
//                   <button
//                     onClick={() => handleConvertOrder(order)}
//                     className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
//                   >
//                     Convert
//                   </button>
//                 )}
//                 <button
//                   onClick={() => handleViewItem(order)}
//                   className="px-3 py-1 text-white bg-gray-600 rounded hover:bg-gray-700"
//                 >
//                   View Items
//                 </button>
//               </div>
//             </div>
//           )) : (
//             <div className="col-span-full p-6 text-center text-gray-500">No orders found.</div>
//           )}
//         </div>
//       ) : (
//         /* TABLE VIEW */
//         <div className="overflow-x-auto rounded-lg shadow-md">
//           <table className="min-w-full divide-y divide-gray-200 text-sm">
//             <thead className="bg-gray-100 text-gray-900">
//               <tr>
//                 {["Order", "Customer", "Order Total", "Pay Ref", "Amount", "Actions"].map((head) => (
//                   <th key={head} className="px-3 py-2 text-left font-semibold uppercase">{head}</th>
//                 ))}
//               </tr>
//             </thead>
//             <tbody className="bg-white divide-y divide-gray-200">
//               {filteredOrders.length > 0 ? filteredOrders.map((order) => (
//                 <tr key={order.Poid} className="hover:bg-gray-50">
//                   <td className="px-3 py-2 font-medium text-gray-800">{order.Poid}</td>
//                   <td className="px-3 py-2">
//                     <div className="font-semibold text-gray-800">{order.Customer}</div>
//                     <div className="text-sm text-gray-600">{order.MobileNo}</div>
//                     <div className="text-sm text-gray-500">{order.Address}</div>
//                   </td>
//                   <td className="px-3 py-2">₹{order.OrderTotal?.toFixed(2)}</td>
//                   <td className="px-3 py-2">{order.PayRef}</td>
//                   <td className="px-3 py-2">₹{order.Amount?.toFixed(2)}</td>
//                   <td className="px-3 py-2 space-y-1">
//                     {!convertedOrders.includes(order.Poid) && (
//                       <button onClick={() => handleConvertOrder(order)} className="w-full px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700">Convert</button>
//                     )}
//                     <button onClick={() => handleViewItem(order)} className="w-full px-3 py-1 text-white bg-gray-600 rounded hover:bg-gray-700">View Items</button>
//                   </td>
//                 </tr>
//               )) : (
//                 <tr><td colSpan="6" className="p-6 text-center text-gray-500">No orders found.</td></tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       )}

//       {/* Modal for viewing items */}
//       <Modal
//         isOpen={isModalOpen}
//         onRequestClose={closeModal}
//         contentLabel="Item Details"
//         className="w-[95%] sm:max-w-2xl p-4 sm:p-6 mx-auto mt-20 bg-white border rounded-lg shadow-lg outline-none"
//         overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center"
//       >
//         <h2 className="mb-4 text-lg font-semibold sm:text-xl">Items for {selectedCustomer}</h2>
//         <div className="overflow-y-auto max-h-80 sm:max-h-96">
//           <ul className="space-y-3 text-sm text-gray-700">
//             {selectedItems.map((item, index) => (
//               <li key={index} className="pb-2 border-b">
//                 <strong>{index + 1}. {item.Partno}</strong> - {item.Partdesc}<br />
//                 Qty: {item.Qty}, Rate: ₹{item.Rate}, Amount: ₹{item.Amount}
//               </li>
//             ))}
//           </ul>
//         </div>
//         <button onClick={closeModal} className="px-4 py-2 mt-6 text-white bg-blue-600 rounded hover:bg-blue-700">Close</button>
//       </Modal>
//     </div>
//   );
// }

